import React, { useEffect, useState, useContext, useMemo } from 'react';
import Layout from '../components/Layout';
import api from '../api/axios';
import { AuthContext } from '../context/AuthContext';
import { formatDate } from '../utils/formatDate';
import { toast } from 'react-toastify';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import {
    ArrowDownTrayIcon,
    MagnifyingGlassIcon,
    FunnelIcon,
    DocumentTextIcon,
    TableCellsIcon,
    ListBulletIcon,
    CalendarDaysIcon,
    UserIcon,
    ChatBubbleBottomCenterTextIcon,
    CheckCircleIcon,
    XCircleIcon,
    ClockIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
    XMarkIcon,
    GlobeAmericasIcon,
    MapPinIcon,
    BoltIcon,
    UsersIcon,
    BanknotesIcon
} from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckSolid } from '@heroicons/react/24/solid';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const localizer = momentLocalizer(moment);

// --- 1. SIMPLE DATE PARSER (Matches your working LeaveCalendar) ---
const toLocalDate = (dateStr) => {
    if (!dateStr) return new Date();
    return new Date(dateStr);
};

// --- 2. HELPERS ---
const getLeaveShortCode = (name, reason = '') => {
    const n = (name || '').toLowerCase();
    const r = (reason || '').toLowerCase();

    if (r.includes('half day') || r.includes('half-day')) return 'HD';
    if (r.includes('permission')) return 'PERM';

    if (n.includes('casual')) return 'CL';
    if (n.includes('sick')) return 'SL';
    if (n.includes('earned') || n.includes('privilege')) return 'EL';
    if (n.includes('maternity')) return 'ML';
    if (n.includes('paternity')) return 'PL';
    if (n.includes('comp') || n.includes('off')) return 'CO';
    if (n.includes('loss') || n.includes('unpaid')) return 'LOP';

    return 'L';
};

const LeaveReports = () => {
    const { user } = useContext(AuthContext);
    const isEmployee = user.role === 'Employee';
    const isManager = user.role === 'Manager';
    const isAdminLike = user.role === 'Admin' || user.role === 'Super Admin';

    const [viewMode, setViewMode] = useState('detailed');
    const [reports, setReports] = useState([]);
    const [summary, setSummary] = useState([]);
    const [balanceSummary, setBalanceSummary] = useState([]);
    const [holidays, setHolidays] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [loading, setLoading] = useState(true);

    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('All');
    const [typeFilter, setTypeFilter] = useState('All');
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    // Manager-only: 'all' | 'mine' | 'team'
    const [scopeFilter, setScopeFilter] = useState('all');
    // Loss of Pay view: one calendar month at a time
    const [lopMonth, setLopMonth] = useState(moment().format('YYYY-MM'));
    const [lopRows, setLopRows] = useState([]);

    const [currentPage, setCurrentPage] = useState(1);
    const PAGE_SIZE = 10;

    const [calendarEvents, setCalendarEvents] = useState([]);
    const [calendarView, setCalendarView] = useState('month');
    const [calendarDate, setCalendarDate] = useState(new Date());
    const [showMoreModal, setShowMoreModal] = useState({ isOpen: false, events: [], date: null });

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line
    }, [viewMode, lopMonth]);

    useEffect(() => {
        filterData();
        // eslint-disable-next-line
    }, [searchTerm, statusFilter, typeFilter, dateFrom, dateTo, reports, summary, lopRows, holidays, viewMode, scopeFilter]);

    const fetchData = async () => {
        setLoading(true);
        try {
            try {
                const calRes = await api.get('/leaves/calendar');
                setHolidays(calRes.data.holidays || []);
            } catch (e) { }

            // Balance summary — only for Manager/Admin (Employee gets 403 on this endpoint)
            if (!isEmployee) {
                try {
                    const sumRes = await api.get('/leaves/summary');
                    const sumData = sumRes.data || [];
                    setBalanceSummary(sumData);
                    if (viewMode === 'summary') {
                        setSummary(sumData);
                        setFilteredData(sumData);
                    }
                } catch (e) {
                    setBalanceSummary([]);
                    if (viewMode === 'summary') { setSummary([]); setFilteredData([]); }
                }
            }

            if (viewMode === 'lop') {
                const res = await api.get('/leaves/lop-summary', { params: { month: lopMonth } });
                setLopRows(res.data.rows || []);
                setFilteredData(res.data.rows || []);
            }

            if (viewMode === 'detailed' || viewMode === 'calendar') {
                const res = await api.get('/leaves/reports');
                setReports(res.data);
                setFilteredData(res.data);
            }
        } catch (err) {
            console.error(err);
            toast.error("Failed to load data");
        } finally {
            setLoading(false);
        }
    };

    const filterData = () => {
        let sourceData = viewMode === 'lop' ? lopRows : (viewMode === 'detailed' || viewMode === 'calendar') ? reports : summary;
        if (!Array.isArray(sourceData)) sourceData = [];

        let temp = [...sourceData];

        // Manager scope filter: 'mine' = only their own, 'team' = exclude their own
        if (isManager && (viewMode === 'detailed' || viewMode === 'calendar')) {
            if (scopeFilter === 'mine') temp = temp.filter(r => r.employee_name === user.name);
            else if (scopeFilter === 'team') temp = temp.filter(r => r.employee_name !== user.name);
        }

        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            temp = temp.filter(item => {
                const name = item.employee_name || item.name || '';
                const email = item.email || '';
                const dept = item.department || '';
                return (name && name.toLowerCase().includes(term)) ||
                    (email && email.toLowerCase().includes(term)) ||
                    (dept && dept.toLowerCase().includes(term));
            });
        }

        if (viewMode === 'detailed' || viewMode === 'calendar') {
            if (statusFilter !== 'All') temp = temp.filter(r => r.status === statusFilter);
            if (typeFilter !== 'All') temp = temp.filter(r => r.type_name === typeFilter);
            if (dateFrom) temp = temp.filter(r => (r.start_date || '').slice(0, 10) >= dateFrom);
            if (dateTo) temp = temp.filter(r => (r.end_date || r.start_date || '').slice(0, 10) <= dateTo);
        }

        setFilteredData(temp);

        // --- CALENDAR LOGIC ---
        if (viewMode === 'calendar') {
            const events = [];

            // A. LEAVES
            temp.forEach(r => {
                const start = toLocalDate(r.start_date);
                const endFromDB = toLocalDate(r.end_date);
                const end = moment(endFromDB).add(1, 'days').toDate();

                let color = '#64748b';
                if (r.status === 'Approved') color = '#059669';
                else if (r.status.includes('Pending')) color = '#d97706';
                else if (r.status === 'Rejected') color = '#e11d48';

                const firstName = r.employee_name.split(' ')[0];
                const shortCode = r.is_unpaid ? 'LOP' : getLeaveShortCode(r.type_name, r.reason);
                const days = r.days_count;

                events.push({
                    id: `leave-${r.id}`,
                    title: `${firstName} - ${shortCode} - ${days}`,
                    start: start,
                    end: end,
                    resource: { ...r, type: 'leave' },
                    color: color,
                    allDay: true
                });
            });

            // B. HOLIDAYS
            holidays.forEach(h => {
                const start = toLocalDate(h.holiday_date);
                const end = moment(start).add(1, 'days').toDate();

                events.push({
                    id: `holiday-${h.id}`,
                    title: h.name,
                    start: start,
                    end: end,
                    allDay: true,
                    resource: { type: 'holiday', country: h.country },
                    color: h.country === 'Global' ? '#9333ea' : '#64748b'
                });
            });

            setCalendarEvents(events);
        }
    };

    const handleConvertToLop = async (r) => {
        const days = window.prompt(`How many of the ${r.days_count} day(s) should be Loss of Pay? (0 to ${r.days_count}, steps of 0.5)`, r.lop_days > 0 ? r.lop_days : r.days_count);
        if (days === null) return;
        const reason = window.prompt('Reason (shown to the employee):');
        if (!reason || !reason.trim()) return toast.error('A reason is required');
        try {
            await api.post('/leaves/convert-lop', { request_id: r.id, lop_days: days, reason });
            toast.success('Loss of Pay updated');
            fetchData();
        } catch (err) { toast.error(err.response?.data?.error || 'Failed to update LOP'); }
    };

    const LOP_COLUMNS = ['Employee', 'Email', 'Department', 'Working Days', 'Paid Leave', 'LOP Days', 'Payable Days'];
    const lopExportRows = () => filteredData.map(r => ({
        "Employee": r.employee_name,
        "Email": r.email,
        "Department": r.department || 'General',
        "Working Days": r.working_days,
        "Paid Leave": r.paid_leave_days,
        "LOP Days": r.lop_days,
        "Payable Days": r.payable_days,
    }));

    const exportToExcel = () => {
        if (viewMode === 'lop') {
            const ws = XLSX.utils.json_to_sheet(lopExportRows());
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, `LOP ${lopMonth}`);
            XLSX.writeFile(wb, `SmartBreak_LOP_${lopMonth}.xlsx`);
            return;
        }
        let dataToExport = [];
        if (viewMode === 'detailed' || viewMode === 'calendar') {
            dataToExport = filteredData.map(r => ({
                "Employee": r.employee_name,
                "Department": r.department || 'General',
                "Leave Type": r.type_name,
                "Start Date": formatDate(r.start_date),
                "End Date": formatDate(r.end_date),
                "Days": r.days_count,
                "LOP Days": r.lop_days || 0,
                "Status": r.status,
                "Approved By": r.approver_name || 'System',
                "Reason": r.reason,
            }));
        } else {
            filteredData.forEach(u => {
                (u.leaves || []).forEach(l => {
                    dataToExport.push({
                        "Employee": u.name,
                        "Department": u.department || 'General',
                        "Email": u.email,
                        "Leave Type": l.type,
                        "Total": l.total,
                        "Used": l.taken,
                        "Available": l.available,
                    });
                });
            });
        }
        const ws = XLSX.utils.json_to_sheet(dataToExport);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Leave Data");
        XLSX.writeFile(wb, `SmartBreak_Report.xlsx`);
    };

    const exportToPDF = () => {
        const doc = new jsPDF({ orientation: 'landscape' });
        const indigo = [79, 70, 229];
        const slate = [71, 85, 105];

        if (viewMode === 'lop') {
            doc.setFontSize(14);
            doc.text(`SmartBreak Loss of Pay — ${moment(lopMonth, 'YYYY-MM').format('MMMM YYYY')}`, 14, 15);
            autoTable(doc, {
                startY: 22,
                head: [LOP_COLUMNS],
                body: lopExportRows().map(r => LOP_COLUMNS.map(c => r[c])),
                styles: { fontSize: 8, cellPadding: 2 },
                headStyles: { fillColor: indigo, fontSize: 8 },
            });
            doc.save(`SmartBreak_LOP_${lopMonth}.pdf`);
            return;
        }

        doc.setFontSize(14);
        doc.text('SmartBreak Leave Report', 14, 15);
        doc.setFontSize(9);
        doc.setTextColor(120);
        const rangeLabel = dateFrom || dateTo
            ? `Period: ${dateFrom || 'Start'} to ${dateTo || 'End'}`
            : 'All Dates';
        doc.text(rangeLabel, 14, 21);
        doc.setTextColor(0);

        if (viewMode === 'detailed' || viewMode === 'calendar') {
            // --- Main table: same columns as Excel ---
            autoTable(doc, {
                startY: 26,
                head: [['Employee', 'Department', 'Leave Type', 'Start Date', 'End Date', 'Days', 'Status', 'Approved By', 'Reason']],
                body: filteredData.map(r => [
                    r.employee_name,
                    r.department || 'General',
                    r.type_name,
                    formatDate(r.start_date),
                    formatDate(r.end_date),
                    r.days_count,
                    r.status,
                    r.approver_name || 'System',
                    r.reason || '',
                ]),
                styles: { fontSize: 7.5, cellPadding: 2 },
                headStyles: { fillColor: indigo, fontSize: 7.5 },
                columnStyles: { 8: { cellWidth: 40 } },
            });

            // --- Per-person summary table (same data as on-screen panel) ---
            if (leaveSummaryByPerson.length > 0) {
                const afterY = doc.lastAutoTable.finalY + 10;
                doc.setFontSize(10);
                doc.setTextColor(...slate);
                doc.text('Leave Summary by Employee', 14, afterY);
                doc.setTextColor(0);

                const allTypes = [...new Set(leaveSummaryByPerson.flatMap(p => Object.keys(p.types)))];

                autoTable(doc, {
                    startY: afterY + 4,
                    head: [['Employee', 'Department', ...allTypes.flatMap(t => [t + ' Used', t + ' Left']), 'Total Used']],
                    body: leaveSummaryByPerson.map(person => [
                        person.name,
                        person.department || 'General',
                        ...allTypes.flatMap(t => [
                            person.types[t] || 0,
                            person.remaining[t] !== undefined ? person.remaining[t].available : '–',
                        ]),
                        person.total,
                    ]),
                    styles: { fontSize: 7, cellPadding: 2 },
                    headStyles: { fillColor: slate, fontSize: 7 },
                    columnStyles: { 0: { cellWidth: 32 }, 1: { cellWidth: 24 } },
                });
            }
        } else {
            // Balance summary view — same columns as Excel
            const rows = [];
            filteredData.forEach(u => {
                (u.leaves || []).forEach(l => {
                    rows.push([u.name, u.department || 'General', u.email, l.type, l.total, l.taken, l.available]);
                });
            });
            autoTable(doc, {
                startY: 26,
                head: [['Employee', 'Department', 'Email', 'Leave Type', 'Total', 'Used', 'Available']],
                body: rows,
                styles: { fontSize: 7.5, cellPadding: 2 },
                headStyles: { fillColor: indigo, fontSize: 7.5 },
            });

            // --- Per-person balance summary table ---
            if (filteredData.length > 0) {
                const allTypes = [...new Set(filteredData.flatMap(u => (u.leaves || []).map(l => l.type)))];
                const afterY = doc.lastAutoTable.finalY + 10;
                doc.setFontSize(10);
                doc.setTextColor(...slate);
                doc.text('Leave Balance Summary by Employee', 14, afterY);
                doc.setTextColor(0);

                autoTable(doc, {
                    startY: afterY + 4,
                    head: [['Employee', 'Department', ...allTypes.flatMap(t => [`${t} Total`, `${t} Used`, `${t} Avail`])]],
                    body: filteredData.map(u => {
                        const leaveMap = {};
                        (u.leaves || []).forEach(l => { leaveMap[l.type] = l; });
                        return [
                            u.name,
                            u.department || 'General',
                            ...allTypes.flatMap(t => [
                                leaveMap[t]?.total ?? 0,
                                leaveMap[t]?.taken ?? 0,
                                leaveMap[t]?.available ?? 0,
                            ]),
                        ];
                    }),
                    styles: { fontSize: 6.5, cellPadding: 1.5 },
                    headStyles: { fillColor: slate, fontSize: 6.5 },
                });
            }
        }

        doc.save('SmartBreak_Report.pdf');
    };

    const getAvatarColor = (name) => {
        if (!name) return 'bg-gray-400';
        const colors = ['bg-indigo-500', 'bg-pink-500', 'bg-cyan-500', 'bg-orange-500', 'bg-violet-500', 'bg-emerald-500'];
        return colors[name.length % colors.length];
    };

    const totalPages = Math.ceil(filteredData.length / PAGE_SIZE);
    const paginatedData = filteredData.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

    // Reset to page 1 whenever filters produce a new result set
    useEffect(() => { setCurrentPage(1); }, [filteredData]);

    // name → leaveType → { total, taken, available } from the balance API
    const balanceLookup = useMemo(() => {
        const lookup = {};
        balanceSummary.forEach(u => {
            lookup[u.name] = {};
            (u.leaves || []).forEach(l => { lookup[u.name][l.type] = l; });
        });
        return lookup;
    }, [balanceSummary]);

    // Per-person leave summary (full filteredData, not just current page)
    const leaveSummaryByPerson = useMemo(() => {
        if (viewMode !== 'detailed') return [];
        const map = {};
        filteredData.forEach(r => {
            if (!r.employee_name || !r.type_name) return;
            const key = r.employee_name;
            if (!map[key]) map[key] = { name: r.employee_name, department: r.department, types: {}, total: 0 };
            map[key].types[r.type_name] = (map[key].types[r.type_name] || 0) + (Number(r.days_count) || 0);
            map[key].total += Number(r.days_count) || 0;
        });
        return Object.values(map).map(person => ({
            ...person,
            remaining: balanceLookup[person.name] || {},
        }));
    }, [filteredData, viewMode, balanceLookup]);

    const leaveTypes = (viewMode === 'detailed' || viewMode === 'calendar')
        ? ['All', ...new Set(reports.map(r => r.type_name))]
        : [];

    // --- COMPONENTS ---
    const EventComponent = ({ event }) => {
        const isHoliday = event.resource.type === 'holiday';
        if (isHoliday) {
            const isGlobal = event.resource.country === 'Global';
            return (
                <div className={`flex items-center space-x-1 text-[10px] font-bold px-1 py-0.5 h-full overflow-hidden truncate ${isGlobal ? 'text-white' : 'text-slate-100'}`}>
                    {isGlobal ? <GlobeAmericasIcon className="h-3 w-3" /> : <MapPinIcon className="h-3 w-3" />}
                    <span>{event.title}</span>
                </div>
            );
        }
        return (
            <div
                className="text-[10px] sm:text-xs font-bold px-1 py-0.5 h-full overflow-hidden truncate"
                title={`Employee: ${event.resource.employee_name}\nType: ${event.resource.type_name}\nDays: ${event.resource.days_count}\nStatus: ${event.resource.status}`}
            >
                {event.title}
            </div>
        );
    };

    const CalendarToolbar = ({ label, onNavigate, onView, view }) => (
        <div className="flex flex-col sm:flex-row justify-between items-center mb-4 p-2 gap-4">
            <div className="flex gap-2">
                <button type="button" onClick={() => onNavigate('PREV')} className="p-1 rounded-full hover:bg-slate-100"><ChevronLeftIcon className="w-5 h-5 text-slate-500" /></button>
                <span className="font-bold text-slate-700 text-lg">{label}</span>
                <button type="button" onClick={() => onNavigate('NEXT')} className="p-1 rounded-full hover:bg-slate-100"><ChevronRightIcon className="w-5 h-5 text-slate-500" /></button>
            </div>
            <div className="flex bg-slate-100 p-1 rounded-lg">
                {['month', 'week', 'day'].map(v => (
                    <button
                        key={v}
                        type="button"
                        onClick={() => onView(v)}
                        className={`px-3 py-1 text-xs font-bold rounded-md capitalize transition ${view === v ? 'bg-white shadow-sm text-indigo-600' : 'text-slate-500 hover:text-slate-700'}`}
                    >
                        {v}
                    </button>
                ))}
            </div>
        </div>
    );

    const handleShowMore = (events, date) => {
        setShowMoreModal({ isOpen: true, events, date });
    };

    if (loading) return (
        <Layout>
            <div className="flex flex-col items-center justify-center h-[80vh] text-slate-400 animate-pulse">
                <DocumentTextIcon className="h-16 w-16 mb-4 opacity-50" />
                <p className="font-medium">Loading Reports...</p>
            </div>
        </Layout>
    );

    return (
        <Layout>
            {/* Header */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
                <div>
                    <h1 className="text-3xl font-black text-slate-800 tracking-tight">
                        {isEmployee
                            ? 'My Leave History'
                            : viewMode === 'lop'
                                ? isManager ? 'Team Loss of Pay' : 'Company Loss of Pay'
                            : isManager
                                ? viewMode === 'calendar' ? 'Team Leave Calendar' : viewMode === 'summary' ? 'Team Balances' : 'Team Leave Reports'
                                : viewMode === 'calendar' ? 'Company Leave Calendar' : viewMode === 'summary' ? 'Company Balances' : 'Company Leave Reports'}
                    </h1>
                    <p className="text-slate-500 mt-1 text-sm font-medium">
                        {isEmployee
                            ? 'View your leave history, status, and remaining balances.'
                            : isManager
                                ? 'Monitor your team\'s leaves and plan coverage.'
                                : 'Full visibility across all employees, departments, and leave types.'}
                    </p>
                </div>
                <div className="flex flex-wrap gap-3">
                    {/* View Toggle — Manager & Admin only */}
                    {!isEmployee && (
                        <div className="flex bg-white p-1 rounded-xl border border-slate-200 shadow-sm">
                            <button onClick={() => setViewMode('detailed')} className={`px-3 sm:px-4 py-2 rounded-lg text-sm font-bold flex items-center transition ${viewMode === 'detailed' ? 'bg-indigo-50 text-indigo-600 shadow-sm' : 'text-slate-500 hover:bg-slate-50'}`}><ListBulletIcon className="w-4 h-4 mr-2" /> Log</button>
                            <button onClick={() => setViewMode('calendar')} className={`px-3 sm:px-4 py-2 rounded-lg text-sm font-bold flex items-center transition ${viewMode === 'calendar' ? 'bg-indigo-50 text-indigo-600 shadow-sm' : 'text-slate-500 hover:bg-slate-50'}`}><CalendarDaysIcon className="w-4 h-4 mr-2" /> Calendar</button>
                            <button onClick={() => setViewMode('summary')} className={`px-3 sm:px-4 py-2 rounded-lg text-sm font-bold flex items-center transition ${viewMode === 'summary' ? 'bg-indigo-50 text-indigo-600 shadow-sm' : 'text-slate-500 hover:bg-slate-50'}`}><TableCellsIcon className="w-4 h-4 mr-2" /> Balances</button>
                            <button onClick={() => setViewMode('lop')} className={`px-3 sm:px-4 py-2 rounded-lg text-sm font-bold flex items-center transition ${viewMode === 'lop' ? 'bg-indigo-50 text-indigo-600 shadow-sm' : 'text-slate-500 hover:bg-slate-50'}`}><BanknotesIcon className="w-4 h-4 mr-2" /> LOP</button>
                        </div>
                    )}
                    <button onClick={exportToExcel} className="flex items-center justify-center px-4 py-2 bg-emerald-600 text-white rounded-xl font-bold shadow-lg hover:bg-emerald-700 transition"><ArrowDownTrayIcon className="w-5 h-5 mr-2" /> Export</button>
                    <button onClick={exportToPDF} className="flex items-center justify-center px-4 py-2 bg-rose-600 text-white rounded-xl font-bold shadow-lg hover:bg-rose-700 transition"><DocumentTextIcon className="w-5 h-5 mr-2" /> PDF</button>
                </div>
            </div>

            {/* Manager scope filter pill */}
            {isManager && (viewMode === 'detailed' || viewMode === 'calendar') && (
                <div className="flex items-center gap-2 mb-5">
                    <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">View:</span>
                    {[['all', 'All'], ['mine', 'My Leaves'], ['team', 'Team Only']].map(([val, label]) => (
                        <button
                            key={val}
                            onClick={() => setScopeFilter(val)}
                            className={`px-4 py-1.5 rounded-full text-xs font-bold border transition ${scopeFilter === val ? 'bg-indigo-600 text-white border-indigo-600 shadow' : 'bg-white text-slate-500 border-slate-200 hover:border-indigo-300'}`}
                        >
                            {label}
                        </button>
                    ))}
                </div>
            )}

            {/* Filters */}
            <div className="bg-white p-4 rounded-2xl shadow-sm border border-slate-100 mb-6 flex flex-col md:flex-row gap-4 items-center">
                {!isEmployee && (
                    <div className="relative flex-1 w-full">
                        <MagnifyingGlassIcon className="absolute left-3 top-3 h-5 w-5 text-slate-400" />
                        <input type="text" placeholder={viewMode === 'calendar' ? "Filter calendar by name or department..." : isManager ? "Search team members..." : "Search by name, email, or department..."} className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none text-sm font-medium" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
                    </div>
                )}
                {viewMode === 'lop' && (
                    <div className="flex items-center gap-2 w-full md:w-auto">
                        <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">Pay Period</label>
                        <input type="month" className="px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl font-medium text-slate-700 outline-none text-sm" value={lopMonth} onChange={e => e.target.value && setLopMonth(e.target.value)} />
                    </div>
                )}
                {(viewMode === 'detailed' || viewMode === 'calendar') && (
                    <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto flex-wrap">
                        <select className="w-full sm:w-36 px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl font-medium text-slate-700 outline-none text-sm" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
                            <option value="All">All Status</option><option value="Approved">Approved</option><option value="Pending">Pending</option><option value="Rejected">Rejected</option>
                        </select>
                        <select className="w-full sm:w-36 px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl font-medium text-slate-700 outline-none text-sm" value={typeFilter} onChange={e => setTypeFilter(e.target.value)}>
                            {leaveTypes.map(t => <option key={t} value={t}>{t}</option>)}
                        </select>
                        <div className="flex items-center gap-2">
                            <div className="relative flex items-center">
                                <CalendarDaysIcon className="absolute left-3 w-4 h-4 text-slate-400 pointer-events-none" />
                                <input
                                    type="date"
                                    className="pl-9 pr-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-medium text-slate-700 outline-none focus:ring-2 focus:ring-indigo-500 w-40"
                                    value={dateFrom}
                                    onChange={e => setDateFrom(e.target.value)}
                                    title="From date"
                                />
                            </div>
                            <span className="text-slate-400 text-sm font-medium">—</span>
                            <div className="relative flex items-center">
                                <CalendarDaysIcon className="absolute left-3 w-4 h-4 text-slate-400 pointer-events-none" />
                                <input
                                    type="date"
                                    className="pl-9 pr-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-medium text-slate-700 outline-none focus:ring-2 focus:ring-indigo-500 w-40"
                                    value={dateTo}
                                    onChange={e => setDateTo(e.target.value)}
                                    title="To date"
                                />
                            </div>
                            {(dateFrom || dateTo) && (
                                <button
                                    onClick={() => { setDateFrom(''); setDateTo(''); }}
                                    className="p-2 text-slate-400 hover:text-rose-500 hover:bg-rose-50 rounded-lg transition"
                                    title="Clear date filter"
                                >
                                    <XMarkIcon className="w-4 h-4" />
                                </button>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* --- CONTENT AREA --- */}
            <div className="bg-white rounded-3xl shadow-sm border border-slate-100 overflow-hidden min-h-[600px]">

                {/* 1. CALENDAR VIEW */}
                {viewMode === 'calendar' && (
                    <div className="p-6 h-[800px]">
                        <Calendar
                            localizer={localizer}
                            events={calendarEvents}
                            startAccessor="start"
                            endAccessor="end"
                            view={calendarView}
                            onView={setCalendarView}
                            date={calendarDate}
                            onNavigate={setCalendarDate}
                            views={['month', 'week', 'day']}
                            style={{ height: '100%' }}
                            onShowMore={handleShowMore}
                            popup={false}
                            eventPropGetter={(event) => ({
                                style: { backgroundColor: event.color, borderRadius: '4px', border: 'none', color: 'white' }
                            })}
                            components={{ event: EventComponent, toolbar: CalendarToolbar }}
                        />
                    </div>
                )}

                {/* 2. DETAILED LIST VIEW */}
                {viewMode === 'detailed' && (
                    <>
                        <div className="overflow-x-auto w-full">
                            <table className={`w-full text-left border-collapse ${isEmployee ? 'min-w-[700px]' : 'min-w-[1000px]'}`}>
                                <thead className="bg-slate-50/80 border-b border-slate-100">
                                    <tr className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                                        {!isEmployee && <th className="px-6 py-4">Employee Details</th>}
                                        <th className="px-6 py-4">Leave Type</th>
                                        <th className="px-6 py-4">Duration & Dates</th>
                                        <th className="px-6 py-4">Status & Workflow</th>
                                        <th className="px-6 py-4">Reason & Remarks</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-50 text-sm">
                                    {paginatedData.map(r => (
                                        <tr key={r.id} className="hover:bg-indigo-50/30 transition-colors group">
                                            {!isEmployee && (
                                            <td className="px-6 py-4 align-top">
                                                <div className="flex items-center">
                                                    <div className={`h-10 w-10 rounded-full flex-shrink-0 flex items-center justify-center text-white font-bold text-sm mr-3 shadow-sm ${getAvatarColor(r.employee_name)}`}>
                                                        {r.employee_name ? r.employee_name.charAt(0) : '?'}
                                                    </div>
                                                    <div>
                                                        <div className="font-bold text-slate-800">{r.employee_name}</div>
                                                        <div className="text-xs text-slate-400">{r.department || 'General'}</div>
                                                    </div>
                                                </div>
                                            </td>
                                            )}
                                            <td className="px-6 py-4 align-top">
                                                <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-bold bg-slate-100 text-slate-700 border border-slate-200">
                                                    {r.type_name}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 align-top">
                                                <div className="flex items-center text-slate-800 font-bold mb-1">
                                                    <span className="text-lg mr-1">{r.days_count}</span> <span className="text-xs font-medium text-slate-500 mt-1">Days</span>
                                                    {r.lop_days > 0 && <span className="ml-2 px-2 py-0.5 rounded-md text-[11px] font-bold bg-rose-50 text-rose-700 border border-rose-100">{r.lop_days} LOP</span>}
                                                </div>
                                                <div className="flex items-center text-xs text-slate-500 bg-slate-50 px-2 py-1 rounded border border-slate-100 w-fit">
                                                    <CalendarDaysIcon className="w-3.5 h-3.5 mr-1.5 text-slate-400" />
                                                    {formatDate(r.start_date)} <span className="mx-1">→</span> {formatDate(r.end_date)}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 align-top">
                                                <div className="flex flex-col gap-1">
                                                    <span className={`inline-flex w-fit items-center px-2.5 py-1 rounded-full text-xs font-bold border ${r.status === 'Approved' ? 'bg-emerald-50 text-emerald-700 border-emerald-100' :
                                                        r.status === 'Rejected' ? 'bg-rose-50 text-rose-700 border-rose-100' :
                                                            'bg-amber-50 text-amber-700 border-amber-100'
                                                        }`}>
                                                        {r.status}
                                                    </span>
                                                    <div className="flex items-center text-[11px] text-slate-400 ml-1">
                                                        <UserIcon className="w-3 h-3 mr-1" />
                                                        {r.status && r.status.includes('Pending') ? 'Waiting: ' : 'By: '}
                                                        <span className="font-semibold text-slate-600 ml-1">{r.approver_name || 'System'}</span>
                                                    </div>
                                                    {isAdminLike && r.status === 'Approved' && !r.is_unpaid && r.type_name !== 'Comp Off Earned' && (
                                                        <button onClick={() => handleConvertToLop(r)} className="w-fit mt-1 text-[11px] font-bold text-rose-600 hover:text-rose-800 hover:underline">
                                                            {r.lop_days > 0 ? 'Change LOP' : 'Mark as LOP'}
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 align-top max-w-xs">
                                                <p className="text-slate-600 text-xs italic bg-slate-50 p-2 rounded border border-slate-100 mb-1">
                                                    "{r.reason}"
                                                </p>
                                                {r.manager_remarks && (
                                                    <div className="flex items-start text-[11px] text-indigo-600 mt-1">
                                                        <ChatBubbleBottomCenterTextIcon className="w-3.5 h-3.5 mr-1 mt-0.5 flex-shrink-0" />
                                                        <span><b>Admin:</b> {r.manager_remarks}</span>
                                                    </div>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination */}
                        {filteredData.length > 0 && (
                            <div className="flex items-center justify-between px-6 py-4 border-t border-slate-100 bg-slate-50/50">
                                <span className="text-sm text-slate-500">
                                    Showing <b>{Math.min((currentPage - 1) * PAGE_SIZE + 1, filteredData.length)}</b>–<b>{Math.min(currentPage * PAGE_SIZE, filteredData.length)}</b> of <b>{filteredData.length}</b> records
                                </span>
                                <div className="flex items-center gap-1">
                                    <button
                                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                        disabled={currentPage === 1}
                                        className="p-2 rounded-lg text-slate-500 hover:bg-white hover:shadow-sm disabled:opacity-30 disabled:cursor-not-allowed transition"
                                    >
                                        <ChevronLeftIcon className="w-4 h-4" />
                                    </button>
                                    {Array.from({ length: totalPages }, (_, i) => i + 1)
                                        .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
                                        .reduce((acc, p, idx, arr) => {
                                            if (idx > 0 && p - arr[idx - 1] > 1) acc.push('…');
                                            acc.push(p);
                                            return acc;
                                        }, [])
                                        .map((p, idx) =>
                                            p === '…' ? (
                                                <span key={`ellipsis-${idx}`} className="px-2 text-slate-400 text-sm">…</span>
                                            ) : (
                                                <button
                                                    key={p}
                                                    onClick={() => setCurrentPage(p)}
                                                    className={`w-8 h-8 rounded-lg text-sm font-bold transition ${currentPage === p ? 'bg-indigo-600 text-white shadow' : 'text-slate-600 hover:bg-white hover:shadow-sm'}`}
                                                >
                                                    {p}
                                                </button>
                                            )
                                        )
                                    }
                                    <button
                                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                        disabled={currentPage === totalPages}
                                        className="p-2 rounded-lg text-slate-500 hover:bg-white hover:shadow-sm disabled:opacity-30 disabled:cursor-not-allowed transition"
                                    >
                                        <ChevronRightIcon className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Per-person leave summary */}
                        {leaveSummaryByPerson.length > 0 && (
                            <div className="border-t-4 border-slate-100 px-6 py-6 bg-slate-50/30">
                                <div className="flex items-center gap-2 mb-4">
                                    <UsersIcon className="w-4 h-4 text-indigo-500" />
                                    <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                                        {isEmployee ? 'My Leave Summary' : isManager ? 'Team Leave Summary' : 'Leave Summary by Employee'}
                                    </h3>
                                    <span className="text-xs text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full">based on current filters</span>
                                </div>
                                <div className="space-y-3">
                                    {leaveSummaryByPerson.map(person => (
                                        <div key={person.name} className="flex items-center gap-4 bg-white rounded-xl border border-slate-100 px-4 py-3 shadow-sm">
                                            <div className={`h-9 w-9 rounded-full flex-shrink-0 flex items-center justify-center text-white font-bold text-sm ${getAvatarColor(person.name)}`}>
                                                {person.name.charAt(0)}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="font-bold text-slate-800 text-sm truncate">{person.name}</div>
                                                <div className="flex flex-wrap gap-2 mt-1.5">
                                                    {Object.entries(person.types).map(([type, used]) => {
                                                        const bal = person.remaining[type];
                                                        return (
                                                            <span key={type} className="inline-flex items-center gap-1 text-[11px] bg-white border border-slate-200 px-2 py-1 rounded-lg shadow-sm font-medium">
                                                                <span className="text-slate-500 font-semibold">{type}</span>
                                                                <span className="w-px h-3 bg-slate-200" />
                                                                <span className="text-indigo-700 font-bold">{used}d used</span>
                                                                {bal !== undefined && (
                                                                    <>
                                                                        <span className="w-px h-3 bg-slate-200" />
                                                                        <span className={`font-bold ${bal.available > 0 ? 'text-emerald-600' : 'text-rose-500'}`}>{bal.available}d left</span>
                                                                    </>
                                                                )}
                                                            </span>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                            <div className="text-right flex-shrink-0">
                                                <div className="text-[10px] text-slate-400 uppercase tracking-wide">Total</div>
                                                <div className="text-2xl font-black text-slate-800 leading-none">{person.total}</div>
                                                <div className="text-[10px] text-slate-400">days</div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </>
                )}

                {/* 3. SUMMARY VIEW (BALANCES) */}
                {viewMode === 'summary' && (
                    <div className="overflow-x-auto w-full">
                        <table className="w-full text-left min-w-[900px]">
                            <thead className="bg-slate-50 border-b border-slate-100">
                                <tr className="text-xs text-slate-400 uppercase tracking-wider font-bold">
                                    <th className="px-6 py-4 w-[25%]">Employee</th>
                                    <th className="px-6 py-4 w-[75%]">Balance Overview</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-50 text-sm">
                                {filteredData.map(u => (
                                    <tr key={u.id} className="hover:bg-slate-50/50 transition-colors">
                                        <td className="px-6 py-6 align-top border-r border-slate-50">
                                            <div className="flex items-center">
                                                <div className={`h-10 w-10 rounded-full flex items-center justify-center text-white font-bold text-sm mr-3 ${getAvatarColor(u.name)}`}>
                                                    {u.name ? u.name.charAt(0) : '?'}
                                                </div>
                                                <div>
                                                    <div className="font-bold text-slate-800 text-base">{u.name}</div>
                                                    <div className="text-xs text-slate-400">{u.department || 'General'}</div>
                                                    <div className="text-xs text-slate-400">{u.email}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-6 bg-slate-50/30">
                                            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
                                                {u.leaves && u.leaves.map((l, idx) => (
                                                    <div key={idx} className="bg-white border border-slate-100 p-3 rounded-xl text-center shadow-sm relative overflow-hidden group hover:border-indigo-200 hover:shadow-md transition-all">
                                                        <div className={`h-1 w-full absolute top-0 left-0 ${l.available > 0 ? 'bg-emerald-400' : 'bg-rose-400'}`}></div>
                                                        <div className="text-[10px] font-bold text-slate-400 uppercase mb-1 truncate" title={l.type}>{l.type}</div>
                                                        <div className="text-2xl font-black text-slate-800">{l.available}</div>
                                                        <div className="text-[10px] text-slate-400 mt-1 flex justify-center gap-2">
                                                            <span>Used: <b>{l.taken}</b></span><span className="text-slate-200">|</span><span>Total: <b>{l.total}</b></span>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* 4. LOSS OF PAY VIEW (per pay period) */}
                {viewMode === 'lop' && filteredData.length > 0 && (
                    <div className="overflow-x-auto w-full">
                        <table className="w-full text-left min-w-[800px]">
                            <thead className="bg-slate-50 border-b border-slate-100">
                                <tr className="text-xs text-slate-400 uppercase tracking-wider font-bold">
                                    <th className="px-6 py-4">Employee</th>
                                    <th className="px-6 py-4 text-right">Working Days</th>
                                    <th className="px-6 py-4 text-right">Paid Leave</th>
                                    <th className="px-6 py-4 text-right">LOP Days</th>
                                    <th className="px-6 py-4 text-right">Payable Days</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-50 text-sm">
                                {filteredData.map(r => (
                                    <tr key={r.user_id} className="hover:bg-slate-50/50 transition-colors">
                                        <td className="px-6 py-4">
                                            <div className="font-bold text-slate-800">{r.employee_name}</div>
                                            <div className="text-xs text-slate-400">{r.department || 'General'} · {r.email}</div>
                                        </td>
                                        <td className="px-6 py-4 text-right font-medium text-slate-600">{r.working_days}</td>
                                        <td className="px-6 py-4 text-right font-medium text-slate-600">{r.paid_leave_days}</td>
                                        <td className={`px-6 py-4 text-right font-black ${r.lop_days > 0 ? 'text-rose-600' : 'text-slate-300'}`}>{r.lop_days}</td>
                                        <td className="px-6 py-4 text-right font-black text-slate-800">{r.payable_days}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* Empty State */}
                {filteredData.length === 0 && (
                    <div className="p-12 text-center flex flex-col items-center justify-center">
                        <div className="bg-slate-50 p-4 rounded-full mb-3">
                            <MagnifyingGlassIcon className="h-8 w-8 text-slate-300" />
                        </div>
                        <p className="text-slate-500 font-medium">No records found.</p>
                        <p className="text-slate-400 text-sm">Try adjusting your search or filters.</p>
                    </div>
                )}
            </div>

            {/* --- DAILY SUMMARY MODAL ("Show More") --- */}
            {showMoreModal.isOpen && (
                <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6 animate-fade-in-up border border-slate-100 max-h-[80vh] flex flex-col">
                        <div className="flex justify-between items-center mb-4 border-b border-slate-100 pb-4">
                            <div>
                                <h3 className="text-xl font-black text-slate-800">
                                    {moment(showMoreModal.date).format('dddd, MMM D, YYYY')}
                                </h3>
                                <p className="text-sm text-slate-500 font-medium">
                                    {showMoreModal.events.length} entries
                                </p>
                            </div>
                            <button
                                onClick={() => setShowMoreModal({ ...showMoreModal, isOpen: false })}
                                className="p-2 hover:bg-slate-100 rounded-full transition-colors"
                            >
                                <XMarkIcon className="w-6 h-6 text-slate-400" />
                            </button>
                        </div>

                        <div className="overflow-y-auto flex-1 pr-2 space-y-3">
                            {showMoreModal.events.map(evt => {
                                const isHoliday = evt.resource.type === 'holiday';
                                return (
                                    <div key={evt.id} className={`flex items-center justify-between p-3 rounded-xl border transition-colors ${isHoliday ? 'bg-purple-50 border-purple-100' : 'bg-slate-50 border-slate-100'}`}>
                                        <div className="flex items-center">
                                            {!isHoliday ? (
                                                <div className={`h-10 w-10 rounded-full flex items-center justify-center text-white font-bold text-sm mr-3 ${getAvatarColor(evt.resource.employee_name)}`}>
                                                    {evt.resource.employee_name ? evt.resource.employee_name.charAt(0) : '?'}
                                                </div>
                                            ) : (
                                                <div className="h-10 w-10 rounded-full flex items-center justify-center bg-purple-200 text-purple-700 mr-3">
                                                    <GlobeAmericasIcon className="h-5 w-5" />
                                                </div>
                                            )}

                                            <div>
                                                <div className="font-bold text-slate-800 text-sm">
                                                    {isHoliday ? evt.title : evt.resource.employee_name}
                                                </div>
                                                <div className="text-xs text-slate-500 font-medium">
                                                    {isHoliday ? evt.resource.country : (evt.resource.department || 'General')}
                                                </div>
                                            </div>
                                        </div>
                                        {!isHoliday && (
                                            <div className="text-right">
                                                <div className="text-xs font-bold text-slate-700 bg-white border border-slate-200 px-2 py-1 rounded mb-1 inline-block">
                                                    {evt.resource.type_name}
                                                </div>
                                                <div className={`text-[10px] font-bold uppercase tracking-wider ${evt.resource.status === 'Approved' ? 'text-emerald-600' : 'text-amber-600'}`}>
                                                    {evt.resource.status}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>

                        <div className="mt-4 pt-4 border-t border-slate-100 flex justify-end">
                            <button
                                onClick={() => setShowMoreModal({ ...showMoreModal, isOpen: false })}
                                className="px-5 py-2 bg-slate-900 text-white text-sm font-bold rounded-xl hover:bg-indigo-600 transition-colors"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

        </Layout>
    );
};

export default LeaveReports;
